import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, Search, Tag, Star, ShieldCheck, ArrowDownAZ } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Input } from '../components/ui/input';
import { brandsAPI, uploadsAPI } from '../services/api';
import { useT } from '../services/i18n';
import { usePageMeta } from '../services/seo';
import { brandPath } from '../services/brandLogos';
import Breadcrumb from '../components/Breadcrumb';

const letterOf = (name) => {
  const c = name.charAt(0).toUpperCase();
  return /[A-Z]/.test(c) ? c : '#';
};

const BrandsPage = () => {
  const { t } = useT();
  const [brands, setBrands] = useState(null);
  const [filter, setFilter] = useState('');
  const [sort, setSort] = useState('az');

  useEffect(() => {
    brandsAPI.getAll().then(list => setBrands(list.filter(b => b.product_count > 0))).catch(() => setBrands([]));
  }, []);

  const byTrust = useMemo(() => [...(brands || [])].filter(b => b.name.toLowerCase().includes(filter.trim().toLowerCase()))
    .sort((a, b) => ((b.total_reviews > 0) - (a.total_reviews > 0)) || (b.avg_rating - a.avg_rating) || (b.total_reviews - a.total_reviews) || a.name.localeCompare(b.name)), [brands, filter]);
  const ratedCount = useMemo(() => (brands || []).filter(b => b.total_reviews > 0).length, [brands]);
  const groups = useMemo(() => {
    const visible = (brands || []).filter(b => b.name.toLowerCase().includes(filter.trim().toLowerCase()));
    const map = {};
    visible.forEach(b => { (map[letterOf(b.name)] = map[letterOf(b.name)] || []).push(b); });
    return Object.keys(map).sort((a, b) => (a === '#' ? 1 : b === '#' ? -1 : a.localeCompare(b))).map(l => [l, map[l]]);
  }, [brands, filter]);

  const letters = useMemo(() => new Set((brands || []).map(b => letterOf(b.name))), [brands]);
  const alphabet = ['#', ...'ABCDEFGHIJKLMNOPQRSTUVWXYZ'];
  const total = groups.reduce((n, [, list]) => n + list.length, 0);
  usePageMeta({
    title: t('brands.metaTitle'),
    description: t('brands.metaDesc', { n: brands ? brands.length : '', list: brands?.length ? ` : ${brands.slice(0, 8).map(b => b.name).join(', ')}…` : '' }),
    path: '/marques',
  });

  const renderCard = (b) => (
                    <Link
                      key={b.name}
                      to={brandPath(b)}
                      data-testid={`brands-card-${b.name}`}
                      className="group bg-white rounded-lg shadow-sm p-4 flex flex-col items-center gap-2 hover:shadow-md hover:-translate-y-0.5 transition-all"
                    >
                      <div className="h-12 w-full flex items-center justify-center">
                        {b.logo ? (
                          <img src={uploadsAPI.resolveUrl(b.logo)} alt={b.name} className="max-h-10 max-w-[120px] object-contain no-placeholder" data-testid={`brands-logo-${b.name}`} />
                        ) : (
                          <span className="text-base font-black text-gray-700 tracking-wide text-center leading-tight" data-testid={`brands-name-${b.name}`}>{b.name}</span>
                        )}
                      </div>
                      <div className="text-center">
                        {b.logo && <p className="text-xs font-medium text-gray-700 group-hover:text-orange-600">{b.name}</p>}
                        <p className="text-[11px] text-gray-400">{b.product_count} produit{b.product_count > 1 ? 's' : ''}</p>
                        {b.total_reviews > 0 ? (
                          <p className="text-[11px] text-gray-700 inline-flex items-center gap-1 mt-0.5" data-testid={`brands-rating-${b.name}`} title={t('brand.trust.title')}>
                            <span className="inline-flex items-center gap-px">{[1, 2, 3, 4, 5].map(i => <Star key={i} size={10} className={i <= Math.round(b.avg_rating) ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'} />)}</span>
                            <span className="font-semibold">{Number(b.avg_rating).toFixed(1)}</span><span className="text-gray-400">({b.total_reviews})</span>
                          </p>
                        ) : <p className="text-[11px] text-gray-300 mt-0.5" data-testid={`brands-norating-${b.name}`}>{t('brands.noReviews')}</p>}
                      </div>
                    </Link>
  );

  return (
    <div className="min-h-screen bg-gray-100">
      <Header />
      <main className="max-w-7xl mx-auto px-4 py-8" data-testid="brands-page">
        <Breadcrumb testId="brands-breadcrumb" items={[{ label: 'Marques' }]} />

        <section className="bg-white rounded-xl shadow-sm p-6 sm:p-8 mb-6">
          <p className="text-xs uppercase tracking-wider text-orange-600 font-semibold flex items-center gap-1"><Tag size={12} /> {t('brands.kicker')}</p>
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mt-1" data-testid="brands-title">{t('brands.all')}</h1>
          <p className="text-gray-500 mt-2" data-testid="brands-count">
            {brands ? t('brands.countAZ', { n: total, count: total }) : t('common.loading')}
          </p>
          <div className="relative mt-5 max-w-md">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input data-testid="brands-search-input" value={filter} onChange={(e) => setFilter(e.target.value)} placeholder={t('brands.searchPh')} className="pl-9 h-11" />
          </div>
          <div className="flex flex-wrap items-center gap-2 mt-5" data-testid="brands-sort">
            <span className="text-xs text-gray-500">{t('brands.sortBy')}</span>
            <button type="button" onClick={() => setSort('az')} data-testid="brands-sort-az" className={`inline-flex items-center gap-1 text-xs font-medium px-3 py-1.5 rounded-full border transition-colors ${sort === 'az' ? 'bg-[#0e0e10] text-white border-[#0e0e10]' : 'bg-white text-gray-700 border-gray-300 hover:border-gray-500'}`}><ArrowDownAZ size={13} /> {t('brands.sortAz')}</button>
            <button type="button" onClick={() => setSort('trust')} data-testid="brands-sort-trust" className={`inline-flex items-center gap-1 text-xs font-medium px-3 py-1.5 rounded-full border transition-colors ${sort === 'trust' ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-gray-700 border-gray-300 hover:border-emerald-500'}`}><ShieldCheck size={13} /> {t('brands.sortTrust')}</button>
          </div>
          <div className={`flex flex-wrap gap-1 mt-3 ${sort === 'trust' ? 'hidden' : ''}`} data-testid="brands-alphabet">
            {alphabet.map(l => letters.has(l) ? (
              <a key={l} href={`#marques-${l === '#' ? 'autres' : l}`} data-testid={`brands-letter-${l}`} className="w-8 h-8 flex items-center justify-center text-sm font-semibold rounded bg-gray-100 text-gray-800 hover:bg-orange-500 hover:text-white transition-colors">{l}</a>
            ) : (
              <span key={l} className="w-8 h-8 flex items-center justify-center text-sm rounded text-gray-300">{l}</span>
            ))}
          </div>
        </section>

        {!brands ? (
          <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-orange-500" /></div>
        ) : groups.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center text-gray-500" data-testid="brands-empty">{t('brands.noMatch', { q: filter })}</div>
        ) : sort === 'trust' ? (
          <section data-testid="brands-trust-list">
            <p className="text-sm text-gray-500 mb-3" data-testid="brands-trust-hint">{t('brands.trustHint', { n: ratedCount, count: ratedCount })}</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
              {byTrust.map(renderCard)}
            </div>
          </section>
        ) : (
          <div className="space-y-6">
            {groups.map(([letter, list]) => (
              <section key={letter} id={`marques-${letter === '#' ? 'autres' : letter}`} className="scroll-mt-24" data-testid={`brands-group-${letter}`}>
                <div className="flex items-center gap-3 mb-3">
                  <span className="w-10 h-10 flex items-center justify-center rounded-lg bg-[#0e0e10] text-white text-lg font-bold">{letter}</span>
                  <span className="text-sm text-gray-500">{list.length} marque{list.length > 1 ? 's' : ''}</span>
                  <div className="flex-1 h-px bg-gray-200" />
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                  {list.map(renderCard)}
                </div>
              </section>
            ))}
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default BrandsPage;
